import { Box, Typography, TextField, Button, CircularProgress, IconButton, InputAdornment, MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import { useState } from "react";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { API_URL } from "../services/authService";

const AddUsers = ({ data, fetch, onClose }) => {
    const [formData, setFormData] = useState({
        username: "",
        password: "",
        email: "",
        role: "admin",
        store_id: data?.store_id || 1,
    });
    const [errors, setErrors] = useState({
        username: "",
        password: "",
        email: ""
    });
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const validateUsername = (username) => {
        if (!username.trim()) return "Username is required";
        if (username.length < 3) return "Username must be at least 3 characters long";
        if (username.length > 20) return "Username must be less than 20 characters";
        return "";
    };

    const validatePassword = (password) => {
        if (!password.trim()) return "Password is required";
        if (password.length < 8) return "Password must be at least 8 characters long";
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Password must contain at least one special character";
        return "";
    };

    const validateEmail = (email) => {
        if (!email) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return "Please enter a valid email password";
        return "";
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate field
        let errorMessage = "";
        switch (field) {
            case "username":
                errorMessage = validateUsername(value);
                break;
            case "password":
                errorMessage = validatePassword(value);
                break;
            case "email":
                errorMessage = validateEmail(value);
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [field]: errorMessage
        }));
    };

    const validateForm = () => {
        const newErrors = {
            username: validateUsername(formData.username),
            password: validatePassword(formData.password),
            email: validateEmail(formData.email)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== "");
    };

    const onCreateUser = async () => {
        if (!validateForm()) {
            setErrorMsg("Please fix all errors before submitting");
            return;
        }

        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            // Get token from localStorage
            const userData = localStorage.getItem('invCreator');
            if (!userData) {
                throw new Error('Authentication token not found');
            }
            const { token } = JSON.parse(userData);

            // Make API call with token
            const response = await axios.post(
                `${API_URL}/signup`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            // console.log(response);

            if (response.statusText === "Created") {
                setSuccessMsg(response.data.message);
                // Reset form
                setFormData({
                    username: "",
                    password: "",
                    email: "",
                    role: "admin",
                    store_id: data.store_id || 1,
                });
                onClose();
                fetch();
            } else {
                setErrorMsg(response.data.message || "Failed to create User");
            }
        } catch (error) {
            console.error("Error creating store:", error);
            setErrorMsg(error.response?.data?.message || "An error occurred while creating the store");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <TextField
                fullWidth
                variant="outlined"
                label="Username"
                value={formData.username}
                onChange={handleChange('username')}
                error={!!errors.username}
                helperText={errors.username}
                disabled={loading}
                sx={{ mb: 2 }}
                inputProps={{
                    maxLength: 20
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                margin="normal"
                onChange={handleChange("password")}
                error={!!errors.password}
                disabled={loading}
                helperText={errors.password}
                autoComplete="current-password"
                sx={{ mb: 2 }}
                inputProps={{
                    maxLength: 15,
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={togglePasswordVisibility}
                                edge="end"
                                disabled={loading}
                            >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <TextField
                fullWidth
                variant="outlined"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
                sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                    labelId="role-select-label"
                    value={formData.role}
                    onChange={handleChange("role")}
                    label="Role"
                >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="cashier">Cashier</MenuItem>
                </Select>
            </FormControl>

            {errorMsg && (
                <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
                    {errorMsg}
                </Typography>
            )}

            <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={onCreateUser}
                disabled={loading}
                sx={{ height: 48 }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Update Store"}
            </Button>

            {successMsg && (
                <Typography color="success.main" sx={{ textAlign: "center", mt: 2 }}>
                    {successMsg}
                </Typography>
            )}
        </Box>
    );
};

export default AddUsers;